import React, { useState, useEffect, useRef } from 'react';
import { STRINGS } from '../../constants/strings.js';
import { LuCornerDownLeft, LuListChecks } from 'react-icons/lu';
import QuickCommandDialog from '../chat/QuickCommandDialog.jsx';

const CommandsContainer = ({
  isVisible,
  onCommandSelect,
  searchTerm = '',
  position = 'top' // 'top' or 'bottom'
}) => {
  const [filteredCommands, setFilteredCommands] = useState([]);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [showDialog, setShowDialog] = useState(false);
  const [selectedCommand, setSelectedCommand] = useState(null);
  
  // Refs for scrolling
  const commandsListRef = useRef(null);
  const selectedItemRefs = useRef({});
  
  // Filter commands based on search term
  useEffect(() => {
    const commands = STRINGS.ASKME.COMMANDS;
    if (!searchTerm || searchTerm === '/') {
      setFilteredCommands(commands);
    } else {
      const filtered = commands.filter(command =>
        command.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        command.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
        command.command.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredCommands(filtered);
    }
    setSelectedIndex(0);
  }, [searchTerm]);

  // Handle command selection
  const handleCommandSelection = (command) => {
    if (command.hasParameters()) {
      // Show dialog for commands with parameters
      setSelectedCommand(command);
      setShowDialog(true);
    } else {
      // Direct execution for commands without parameters
      onCommandSelect(command.generateCommand());
    }
  };

  // Handle dialog submission
  const handleDialogSubmit = (finalCommand) => {
    onCommandSelect(finalCommand);
    setShowDialog(false);
    setSelectedCommand(null);
  };

  // Handle dialog close
  const handleDialogClose = () => {
    setShowDialog(false);
    setSelectedCommand(null);
  };

  // Scroll selected item into view
  const scrollSelectedItemIntoView = () => {
    const selectedItem = selectedItemRefs.current[selectedIndex];
    if (selectedItem && commandsListRef.current) {
      selectedItem.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
        inline: 'nearest'
      });
    }
  };

  // Scroll selected item into view when selectedIndex changes
  useEffect(() => {
    if (isVisible && filteredCommands.length > 0) {
      scrollSelectedItemIntoView();
    }
  }, [selectedIndex, isVisible, filteredCommands.length]);

  // Handle keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!isVisible) return;
      
      if (showDialog) return;

      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setSelectedIndex(previousIndex =>
          previousIndex < filteredCommands.length - 1 ? previousIndex + 1 : 0
        );
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setSelectedIndex(previousIndex =>
          previousIndex > 0 ? previousIndex - 1 : filteredCommands.length - 1
        );
      } else if (e.key === 'Enter' && filteredCommands[selectedIndex]) {
        e.preventDefault();
        handleCommandSelection(filteredCommands[selectedIndex]);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isVisible, filteredCommands, selectedIndex, onCommandSelect, showDialog]);

  if (!isVisible || filteredCommands.length === 0) {
    return null;
  }

  return (
    <div className={`absolute right-0 left-0 z-[60] ${position === 'top' ? 'bottom-full mb-2' : 'top-full mt-2'}`}>
      <div className="animate-enter overflow-hidden rounded-xl border border-ui-line bg-ui-card shadow-pop">
        <div className="flex items-center gap-2 border-b border-ui-line-soft px-4 py-2.5">
          <LuListChecks aria-hidden className="size-4 text-brand-600 dark:text-brand-300" />
          <span className="text-sm font-semibold text-ui-ink">Ready-made questions</span>
          <span className="ml-auto text-xs text-ui-ink-3">↑ ↓ to move · Enter to pick · Esc to close</span>
        </div>

        <div className="ui-scroll max-h-72 overflow-y-auto p-1.5" ref={commandsListRef} role="listbox">
          {filteredCommands.map((command, index) => {
            const isSelected = index === selectedIndex;
            return (
              <div
                key={command.id}
                ref={(element) => {
                  if (element) {
                    selectedItemRefs.current[index] = element;
                  }
                }}
                role="option"
                aria-selected={isSelected}
                onClick={() => handleCommandSelection(command)}
                onMouseEnter={() => setSelectedIndex(index)}
                className={`flex cursor-pointer items-start gap-3 rounded-lg px-3 py-2.5 transition-colors ${
                  isSelected ? 'bg-ui-brand-soft' : ''
                }`}
              >
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-ui-ink">{command.title}</span>
                    <span className="rounded bg-ui-muted px-1.5 py-0.5 text-[11px] font-medium text-ui-ink-3">
                      {STRINGS.ASKME.CATEGORIES[command.category]}
                    </span>
                  </div>
                  <p className="mt-0.5 text-[13px] leading-snug text-ui-ink-2">{command.description}</p>
                </div>
                {isSelected && <LuCornerDownLeft aria-hidden className="mt-1 size-4 shrink-0 text-ui-ink-3" />}
              </div>
            );
          })}
        </div>
      </div>

      <QuickCommandDialog
        isOpen={showDialog}
        onClose={handleDialogClose}
        command={selectedCommand}
        onSubmit={handleDialogSubmit}
      />
    </div>
  );
};

export default CommandsContainer;
